import React, { useEffect, useMemo, useState } from "react";
import { jwtDecode } from "jwt-decode";
import { motion } from "framer-motion";
import {
  Layers,
  ClipboardList,
  Users,
  CheckCircle2,
  ChevronRight,
  Plus,
  CalendarDays,
  Loader2,
  TrendingUp,
  ArrowRightLeft,
  MapPin,
  Wrench,
} from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  CartesianGrid,
} from "recharts";
import API from "../api/api"; // adjust path if needed

const StatCard = ({ icon: Icon, title, value, delta, deltaLabel, accent }) => (
  <motion.div
    whileHover={{ y: -2 }}
    className="rounded-2xl bg-white/70 dark:bg-gray-800/70 backdrop-blur border border-gray-200 dark:border-gray-700 shadow-sm p-5"
  >
    <div className="flex items-center justify-between">
      <div className={`p-2.5 rounded-xl ${accent}`}>
        <Icon className="h-5 w-5 text-white" />
      </div>
      {delta !== undefined && (
        <span
          className={`text-xs font-medium px-2 py-1 rounded-lg ${
            delta >= 0
              ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300"
              : "bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300"
          }`}
        >
          {delta >= 0 ? "+" : ""}
          {delta}% {deltaLabel}
        </span>
      )}
    </div>
    <div className="mt-4">
      <p className="text-xs text-gray-500 dark:text-gray-400">{title}</p>
      <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
        {value}
      </p>
    </div>
  </motion.div>
);

const SectionCard = ({ title, children, action, className = "" }) => (
  <div
    className={`rounded-2xl bg-white/70 dark:bg-gray-800/70 backdrop-blur border border-gray-200 dark:border-gray-700 shadow-sm ${className}`}
  >
    <div className="flex items-center justify-between p-4 border-b border-gray-200/70 dark:border-gray-700/70">
      <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100">
        {title}
      </h3>
      {action}
    </div>
    <div className="p-4">{children}</div>
  </div>
);

const Skeleton = ({ className = "" }) => (
  <div className={`animate-pulse rounded-lg bg-gray-200 dark:bg-gray-700 ${className}`} />
);

const Home = () => {
  // -------- Auth / greeting ----------
  const token = localStorage.getItem("access_token");
  let username = "there";
  if (token) {
    try {
      const decoded = jwtDecode(token);
      username = decoded?.username || decoded?.sub || "there";
    } catch (err) {
      // ignore
    }
  }

  // -------- State ----------
  const [loading, setLoading] = useState(true);
  const [jobs, setJobs] = useState([]);
  const [runs, setRuns] = useState([]); // surveys/runs
  const [customers, setCustomers] = useState([]);
  const [services, setServices] = useState([]);
  const [range, setRange] = useState("30d"); // 7d | 30d | 90d

  // -------- Fetch ----------
  useEffect(() => {
    let isMounted = true;
    const fetchAll = async () => {
      try {
        const [jobsRes, runsRes, customersRes, servicesRes] = await Promise.all([
          API.get("/jobs/"),
          API.get("/runs/"),
          API.get("/customer/"),
          API.get("/service/"),
        ]);
        if (!isMounted) return;
        setJobs(jobsRes.data || []);
        setRuns(runsRes.data || []);
        setCustomers(customersRes.data || []);
        setServices(servicesRes.data || []);
      } catch (e) {
        // Optional: toast error
        console.error(e);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchAll();
    return () => {
      isMounted = false;
    };
  }, []);

  // -------- Derive metrics ----------
  const today = new Date();
  const days = range === "7d" ? 7 : range === "30d" ? 30 : 90;
  const sinceDate = new Date(today.getTime() - days * 24 * 60 * 60 * 1000);

  const jobsInRange = useMemo(
    () =>
      (jobs || []).filter((j) => {
        const d = new Date(j?.created_date || j?.start_date || today);
        return d >= sinceDate && d <= today;
      }),
    [jobs, sinceDate, today]
  );

  const runsInRange = useMemo(
    () =>
      (runs || []).filter((r) => {
        const d = new Date(r?.created_at || r?.run_date || today);
        return d >= sinceDate && d <= today;
      }),
    [runs, sinceDate, today]
  );

  const totalJobs = jobs?.length || 0;
  const activeSurveys = runs?.filter((r) => !r?.is_completed)?.length || 0;
  const totalCustomers = customers?.length || 0;

  // Fake completion rate: completed runs / total runs
  const completionRate = useMemo(() => {
    const t = runs?.length || 0;
    if (!t) return 0;
    const done = runs.filter((r) => r?.is_completed)?.length || 0;
    return Math.round((done / t) * 100);
  }, [runs]);

  // Trends (simple deltas between this period and previous equal span)
  const calcDelta = (arr, accessorDate) => {
    const prevStart = new Date(sinceDate.getTime() - days * 24 * 60 * 60 * 1000);
    const prevEnd = sinceDate;
    const cur = arr.filter((x) => {
      const d = new Date(accessorDate(x));
      return d >= sinceDate && d <= today;
    }).length;
    const prev = arr.filter((x) => {
      const d = new Date(accessorDate(x));
      return d >= prevStart && d <= prevEnd;
    }).length;
    if (prev === 0 && cur > 0) return 100;
    if (prev === 0 && cur === 0) return 0;
    return Math.round(((cur - prev) / prev) * 100);
  };

  const jobsDelta = calcDelta(jobs || [], (j) => j?.created_date || j?.start_date || today);
  const runsDelta = calcDelta(runs || [], (r) => r?.created_at || r?.run_date || today);

  // Charts: daily counts in range
  const byDay = (arr, accessorDate) => {
    const map = new Map();
    for (let i = 0; i < days; i++) {
      const d = new Date(today.getTime() - (days - 1 - i) * 24 * 60 * 60 * 1000);
      const key = d.toISOString().slice(0, 10);
      map.set(key, 0);
    }
    arr.forEach((item) => {
      const d = new Date(accessorDate(item));
      const key = d.toISOString().slice(0, 10);
      if (map.has(key)) map.set(key, (map.get(key) || 0) + 1);
    });
    return Array.from(map.entries()).map(([date, count]) => ({ date, count }));
  };

  const jobsDaily = useMemo(
    () => byDay(jobsInRange, (j) => j?.created_date || j?.start_date || today),
    [jobsInRange, days, today]
  );
  const runsDaily = useMemo(
    () => byDay(runsInRange, (r) => r?.created_at || r?.run_date || today),
    [runsInRange, days, today]
  );

  // Services usage (bar)
  const serviceUsage = useMemo(() => {
    const map = new Map();
    (jobsInRange || []).forEach((j) => {
      const key = j?.service_name; // likely service_id (number)
      if (!key) return;
      map.set(key, (map.get(key) || 0) + 1);
    });
    // Resolve id -> name
    const idToName = new Map(services.map((s) => [s.service_id, s.service_name]));
    return Array.from(map.entries())
      .map(([id, count]) => ({
        name: idToName.get(id) || `Service ${id}`,
        count,
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 6);
  }, [jobsInRange, services]);

  // Recent jobs (latest first by start_date or created_date)
  const recentJobs = useMemo(() => {
    const s = [...(jobs || [])].sort((a, b) => {
      const da = new Date(a?.start_date || a?.created_date || 0).getTime();
      const db = new Date(b?.start_date || b?.created_date || 0).getTime();
      return db - da;
    });
    return s.slice(0, 8);
  }, [jobs]);

  // -------- UI ----------
  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-950">
      {/* Hero / Header */}
      <div className="relative overflow-hidden">
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-red-600 via-red-500 to-gray-700 opacity-90" />
        <svg
          className="absolute -bottom-24 right-[-60px] h-[280px] w-[480px] opacity-30"
          viewBox="0 0 600 600"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M300,532.6C385,532.6,462.1,484.2,500,412C537.9,339.8,524.1,244.4,466.5,184.3C408.8,124.3,307.4,99.5,223.1,125.2C138.8,150.8,71.6,226.9,58.6,314.9C45.6,402.9,86.7,503,164.4,538.7C242,574.4,315,532.6,300,532.6Z"
            fill="url(#g1)"
          />
          <defs>
            <linearGradient id="g1" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#ef4444" />
              <stop offset="100%" stopColor="#374151" />
            </linearGradient>
          </defs>
        </svg>

        <div className="relative max-w-7xl mx-auto px-6 py-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-white">
                Welcome, {username} 👋
              </h1>
              <p className="text-white/90 mt-1 text-sm">
                Survey Management System — overview & insights at a glance.
              </p>
            </div>

            {/* Range picker */}
            <div className="flex items-center gap-2">
              {["7d", "30d", "90d"].map((r) => (
                <button
                  key={r}
                  onClick={() => setRange(r)}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition ${
                    range === r
                      ? "bg-white text-gray-900 shadow"
                      : "bg-white/20 text-white hover:bg-white/30"
                  }`}
                >
                  {r.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 -mt-6 pb-10 space-y-6">
        {/* KPIs */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {loading ? (
            <>
              <Skeleton className="h-28" />
              <Skeleton className="h-28" />
              <Skeleton className="h-28" />
              <Skeleton className="h-28" />
            </>
          ) : (
            <>
              <StatCard
                icon={Layers}
                title="Total Jobs"
                value={totalJobs}
                delta={jobsDelta}
                deltaLabel="vs prev"
                accent="bg-gradient-to-r from-red-600 to-gray-700"
              />
              <StatCard
                icon={ClipboardList}
                title="Active Surveys"
                value={activeSurveys}
                delta={runsDelta}
                deltaLabel="vs prev"
                accent="bg-gradient-to-r from-indigo-600 to-blue-600"
              />
              <StatCard
                icon={Users}
                title="Customers"
                value={totalCustomers}
                accent="bg-gradient-to-r from-amber-500 to-orange-600"
              />
              <StatCard
                icon={CheckCircle2}
                title="Completion Rate"
                value={`${completionRate}%`}
                accent="bg-gradient-to-r from-emerald-600 to-teal-600"
              />
            </>
          )}
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <SectionCard
            title="Jobs Created"
            action={
              <span className="inline-flex items-center text-xs text-gray-500 dark:text-gray-400">
                <TrendingUp className="h-4 w-4 mr-1" />
                {jobsDelta >= 0 ? "+" : ""}
                {jobsDelta}%
              </span>
            }
            className="lg:col-span-2"
          >
            {loading ? (
              <Skeleton className="h-64" />
            ) : (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={jobsDaily}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                    <XAxis dataKey="date" fontSize={12} />
                    <YAxis allowDecimals={false} fontSize={12} />
                    <Tooltip />
                    <Line
                      type="monotone"
                      dataKey="count"
                      stroke="#ef4444"
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </SectionCard>

          <SectionCard title="Top Services">
            {loading ? (
              <Skeleton className="h-64" />
            ) : (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={serviceUsage}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                    <XAxis dataKey="name" fontSize={12} />
                    <YAxis allowDecimals={false} fontSize={12} />
                    <Tooltip />
                    <Bar dataKey="count" fill="#374151" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </SectionCard>
        </div>

        {/* Recent Jobs / Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <SectionCard
            title="Recent Jobs"
            action={
              <button className="text-xs inline-flex items-center gap-1 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white">
                View All <ChevronRight className="h-4 w-4" />
              </button>
            }
            className="lg:col-span-2"
          >
            {loading ? (
              <div className="space-y-2">
                <Skeleton className="h-10" />
                <Skeleton className="h-10" />
                <Skeleton className="h-10" />
              </div>
            ) : recentJobs.length === 0 ? (
              <div className="text-sm text-gray-500 dark:text-gray-400">
                No jobs yet.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 z-10 bg-gray-50 dark:bg-gray-700 text-gray-600 dark:text-gray-200">
                    <tr>
                      <th className="px-3 py-2 text-left">Job #</th>
                      <th className="px-3 py-2 text-left">Service</th>
                      <th className="px-3 py-2 text-left">Rig</th>
                      <th className="px-3 py-2 text-left">Well</th>
                      <th className="px-3 py-2 text-left">Start</th>
                      <th className="px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentJobs.map((j, idx) => (
                      <tr
                        key={j.id || idx}
                        className="border-t border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/60 transition"
                      >
                        <td className="px-3 py-2 font-medium text-gray-900 dark:text-gray-100">
                          {j.job_number || "-"}
                        </td>
                        <td className="px-3 py-2 text-gray-700 dark:text-gray-200">
                          {typeof j.service_name === "number"
                            ? `#${j.service_name}`
                            : j.service_name || "-"}
                        </td>
                        <td className="px-3 py-2 text-gray-700 dark:text-gray-200">
                          {j.rig_number || "-"}
                        </td>
                        <td className="px-3 py-2 text-gray-700 dark:text-gray-200">
                          {j.well_name || "-"}
                        </td>
                        <td className="px-3 py-2 text-gray-700 dark:text-gray-200">
                          {j.start_date || j.created_date || "-"}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <button className="inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-700">
                            Open <ChevronRight className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </SectionCard>

          <SectionCard
            title="Quick Actions"
            className="lg:col-span-1"
            action={
              <span className="text-[11px] text-gray-500 dark:text-gray-400">
                Speed up your workflow
              </span>
            }
          >
            <div className="grid grid-cols-1 gap-2">
              <button className="flex items-center justify-between w-full px-3 py-2 rounded-xl bg-gradient-to-r from-red-600 to-gray-700 text-white hover:from-red-700 hover:to-gray-800 transition">
                <span className="inline-flex items-center gap-2">
                  <Plus className="h-4 w-4" />
                  New Project / Job
                </span>
                <ChevronRight className="h-4 w-4" />
              </button>

              <button className="flex items-center justify-between w-full px-3 py-2 rounded-xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition text-gray-800 dark:text-gray-100">
                <span className="inline-flex items-center gap-2">
                  <CalendarDays className="h-4 w-4" />
                  Schedule Survey
                </span>
                <ChevronRight className="h-4 w-4" />
              </button>

              <button className="flex items-center justify-between w-full px-3 py-2 rounded-xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition text-gray-800 dark:text-gray-100">
                <span className="inline-flex items-center gap-2">
                  <Wrench className="h-4 w-4" />
                  Configure Services
                </span>
                <ChevronRight className="h-4 w-4" />
              </button>

              <button className="flex items-center justify-between w-full px-3 py-2 rounded-xl bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition text-gray-800 dark:text-gray-100">
                <span className="inline-flex items-center gap-2">
                  <ArrowRightLeft className="h-4 w-4" />
                  Import / Export
                </span>
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>

            {/* Small tips */}
            <div className="mt-4 rounded-xl bg-gray-50 dark:bg-gray-900 border border-dashed border-gray-200 dark:border-gray-700 p-3 text-[12px] text-gray-600 dark:text-gray-300">
              <p className="font-medium mb-1">Tips</p>
              <ul className="list-disc ml-4 space-y-1">
                <li>Use range chips (7d/30d/90d) to explore trends.</li>
                <li>Open a recent job to continue where you left off.</li>
                <li>Dark mode supported out of the box.</li>
              </ul>
            </div>
          </SectionCard>
        </div>

        {/* Secondary widgets */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <SectionCard title="Survey Activity (Daily)">
            {loading ? (
              <Skeleton className="h-56" />
            ) : (
              <div className="h-56">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={runsDaily}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                    <XAxis dataKey="date" fontSize={12} />
                    <YAxis allowDecimals={false} fontSize={12} />
                    <Tooltip />
                    <Line
                      type="monotone"
                      dataKey="count"
                      stroke="#16a34a"
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </SectionCard>

          <SectionCard title="Where We’re Working">
            <div className="flex items-center gap-3 text-sm text-gray-600 dark:text-gray-300">
              <MapPin className="h-4 w-4" />
              <span>
                Surface this from your <code>project.country</code> or job meta.
              </span>
            </div>
            <div className="mt-3 text-xs text-gray-500 dark:text-gray-400">
              (Hook this up to a map later if you’d like.)
            </div>
          </SectionCard>

          <SectionCard title="System Health">
            <div className="flex items-center gap-2 text-sm">
              <Loader2 className="h-4 w-4 animate-spin text-emerald-500" />
              <span className="text-gray-700 dark:text-gray-200">
                All services operational
              </span>
            </div>
            <ul className="mt-3 text-xs text-gray-500 dark:text-gray-400 space-y-1">
              <li>API latency: normal</li>
              <li>DB connections: healthy</li>
              <li>Workers: idle</li>
            </ul>
          </SectionCard>
        </div>
      </div>
    </div>
  );
};

export default Home;
